import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { Input } from '../../components/ui/Input';
import { Badge } from '../../components/ui/Badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/Select';
import {
  Activity, RefreshCw, AlertTriangle, CheckCircle, Clock, X, 
  Mail, Server, Database, BarChart3, TrendingUp, PieChart, 
  Download, Upload, Settings, TestTube, Globe, Lock, Shield,
  Bell, Users, MessageSquare, Calendar, FileText, Monitor,
  Cpu, HardDrive, Network, Wifi, Signal, Battery, Volume,
  Volume2, VolumeX, Play, Pause, Stop, SkipBack, SkipForward,
  RotateCcw, RotateCw, Maximize, Minimize, Maximize2, Minimize2,
  Square, Circle, Triangle, Hexagon, Octagon, Pentagon, Diamond,
  Star, Heart, Coffee, Pizza, Cake, Music, Camera, Video, File,
  Share, Bookmark, Flag, Target, Eye, Edit3, Trash2, Plus,
  Search, Filter, Save, Copy, ExternalLink, Zap, ArrowUp,
  ArrowDown, ArrowLeft, ArrowRight, ChevronUp, ChevronDown,
  ChevronLeft, ChevronRight, MoreHorizontal, MoreVertical
} from 'lucide-react';
import MockDataService from '../../services/MockDataService';

const EmailDiagnostics = () => {
  const [diagnostics, setDiagnostics] = useState(null);
  const [emailAccounts, setEmailAccounts] = useState([]);
  const [logs, setLogs] = useState([]);
  const [metrics, setMetrics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedAccount, setSelectedAccount] = useState('all');
  const [logLevel, setLogLevel] = useState('all');
  const [autoRefresh, setAutoRefresh] = useState(true);

  useEffect(() => {
    const fetchDiagnostics = async () => {
      try {
        setLoading(true);
        const [diagnosticsRes, accountsRes, logsRes, metricsRes] = await Promise.all([
          MockDataService.getEmailDiagnostics(),
          MockDataService.getEmailAccounts(),
          MockDataService.getEmailLogs(),
          MockDataService.getEmailMetrics()
        ]);

        if (diagnosticsRes && diagnosticsRes.success) setDiagnostics(diagnosticsRes.data || null);
        if (accountsRes && accountsRes.success) {
          const accountsData = Array.isArray(accountsRes.data) ? accountsRes.data : [];
          setEmailAccounts(accountsData);
        } else {
          setEmailAccounts([]);
        }
        if (logsRes && logsRes.success) {
          const logsData = Array.isArray(logsRes.data) ? logsRes.data : [];
          setLogs(logsData);
        } else {
          setLogs([]);
        }
        if (metricsRes && metricsRes.success) setMetrics(metricsRes.data || null);
      } catch (err) {
        setError('Error loading email diagnostics');
        console.error('Email diagnostics error:', err);
        setDiagnostics(null);
        setEmailAccounts([]);
        setLogs([]);
        setMetrics(null);
      } finally {
        setLoading(false);
      }
    };

    fetchDiagnostics();

    // Auto-refresh every 30 seconds
    let interval;
    if (autoRefresh) {
      interval = setInterval(fetchDiagnostics, 30000);
    }

    return () => {
      if (interval) clearInterval(interval);
    };
  }, [autoRefresh]);

  const handleRefresh = async () => {
    setLoading(true);
    try {
      const [diagnosticsRes, logsRes, metricsRes] = await Promise.all([
        MockDataService.getEmailDiagnostics(),
        MockDataService.getEmailLogs(),
        MockDataService.getEmailMetrics()
      ]);

      if (diagnosticsRes.success) setDiagnostics(diagnosticsRes.data);
      if (logsRes.success) setLogs(logsRes.data);
      if (metricsRes.success) setMetrics(metricsRes.data);
    } catch (err) {
      console.error('Refresh failed:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleTestConnection = async (accountId) => {
    try {
      // Simulate connection test
      await new Promise(resolve => setTimeout(resolve, 2000));
      alert('Connection test completed successfully!');
      handleRefresh();
    } catch (err) {
      alert('Connection test failed. Please check the logs for details.');
    }
  };

  const filteredLogs = logs.filter(log => {
    const matchesAccount = selectedAccount === 'all' || log.accountId === selectedAccount;
    const matchesLevel = logLevel === 'all' || log.level === logLevel;
    return matchesAccount && matchesLevel;
  });

  const getStatusColor = (status) => {
    switch (status) {
      case 'healthy': return 'bg-green-100 text-green-800';
      case 'warning': return 'bg-yellow-100 text-yellow-800';
      case 'error': return 'bg-red-100 text-red-800';
      case 'offline': return 'bg-gray-100 text-gray-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'healthy': return CheckCircle;
      case 'warning': return AlertTriangle;
      case 'error': return X;
      case 'offline': return Clock;
      default: return Clock;
    }
  };

  const getLogLevelColor = (level) => {
    switch (level) {
      case 'info': return 'bg-blue-100 text-blue-800';
      case 'warning': return 'bg-yellow-100 text-yellow-800';
      case 'error': return 'bg-red-100 text-red-800';
      case 'success': return 'bg-green-100 text-green-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  if (loading && !diagnostics) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <RefreshCw className="h-8 w-8 animate-spin mx-auto mb-4 text-blue-600" />
          <p className="text-gray-600">Loading email diagnostics...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <AlertTriangle className="h-8 w-8 mx-auto mb-4 text-red-600" />
          <p className="text-red-600">{error}</p>
          <Button onClick={() => window.location.reload()} className="mt-4">
            <RefreshCw className="h-4 w-4 mr-2" />
            Retry
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">Email Diagnostics</h1>
          <p className="text-gray-600 dark:text-gray-400 mt-1">
            Monitor email system health and diagnose issues
          </p>
        </div>
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-2">
            <input
              type="checkbox"
              id="autoRefresh"
              checked={autoRefresh}
              onChange={(e) => setAutoRefresh(e.target.checked)}
              className="mr-2"
            />
            <label htmlFor="autoRefresh" className="text-sm text-gray-600 dark:text-gray-400">
              Auto-refresh
            </label>
          </div>
          <Button onClick={handleRefresh} disabled={loading}>
            <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>
      </div>

      {/* System Status Overview */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600 dark:text-gray-400">System Status</p>
                <p className="text-2xl font-bold text-gray-900 dark:text-gray-100">
                  {diagnostics?.systemStatus || 'Unknown'}
                </p>
              </div>
              <div className="p-2 bg-blue-100 rounded-lg">
                <Activity className="h-6 w-6 text-blue-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600 dark:text-gray-400">Active Accounts</p>
                <p className="text-2xl font-bold text-gray-900 dark:text-gray-100">
                  {emailAccounts.filter(acc => acc.status === 'active').length}
                </p>
              </div>
              <div className="p-2 bg-green-100 rounded-lg">
                <Mail className="h-6 w-6 text-green-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600 dark:text-gray-400">Queue Size</p>
                <p className="text-2xl font-bold text-gray-900 dark:text-gray-100">
                  {diagnostics?.queueSize || 0}
                </p>
              </div>
              <div className="p-2 bg-yellow-100 rounded-lg">
                <Database className="h-6 w-6 text-yellow-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600 dark:text-gray-400">Last Fetch</p>
                <p className="text-sm font-medium text-gray-900 dark:text-gray-100">
                  {diagnostics?.lastFetch ? new Date(diagnostics.lastFetch).toLocaleTimeString() : 'Never'}
                </p>
              </div>
              <div className="p-2 bg-purple-100 rounded-lg">
                <Clock className="h-6 w-6 text-purple-600" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Email Accounts Status */}
      <Card className="mb-6">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Mail className="h-5 w-5" />
            Email Accounts Status
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {emailAccounts.map((account) => {
              const StatusIcon = getStatusIcon(account.status);
              return (
                <div key={account.id} className="flex items-center justify-between p-4 border rounded-lg">
                  <div className="flex items-center gap-4">
                    <div className="p-2 bg-blue-100 rounded-lg">
                      <Mail className="h-5 w-5 text-blue-600" />
                    </div>
                    <div>
                      <h3 className="font-medium text-gray-900 dark:text-gray-100">
                        {account.name}
                      </h3>
                      <p className="text-sm text-gray-600 dark:text-gray-400">
                        {account.email}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <Badge className={getStatusColor(account.status)}>
                      <StatusIcon className="h-3 w-3 mr-1" />
                      {account.status}
                    </Badge>
                    <div className="text-sm text-gray-500">
                      Last check: {account.lastCheck ? new Date(account.lastCheck).toLocaleTimeString() : 'Never'}
                    </div>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleTestConnection(account.id)}
                    >
                      <TestTube className="h-4 w-4 mr-1" />
                      Test
                    </Button>
                  </div>
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>

      {/* Performance Metrics */}
      {metrics && (
        <Card className="mb-6">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <BarChart3 className="h-5 w-5" />
              Performance Metrics
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div>
                <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Fetch Performance
                </h4>
                <div className="space-y-2">
                  <div className="flex justify-between text-sm">
                    <span>Average fetch time:</span>
                    <span className="font-medium">{metrics.fetchTime}ms</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span>Success rate:</span>
                    <span className="font-medium">{metrics.successRate}%</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span>Emails processed:</span>
                    <span className="font-medium">{metrics.emailsProcessed}</span>
                  </div>
                </div>
              </div>

              <div>
                <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Queue Statistics
                </h4>
                <div className="space-y-2">
                  <div className="flex justify-between text-sm">
                    <span>Pending emails:</span>
                    <span className="font-medium">{metrics.pendingEmails}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span>Processed today:</span>
                    <span className="font-medium">{metrics.processedToday}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span>Failed today:</span>
                    <span className="font-medium text-red-600">{metrics.failedToday}</span>
                  </div>
                </div>
              </div>

              <div>
                <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  System Health
                </h4>
                <div className="space-y-2">
                  <div className="flex justify-between text-sm">
                    <span>Memory usage:</span>
                    <span className="font-medium">{metrics.memoryUsage}MB</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span>CPU usage:</span>
                    <span className="font-medium">{metrics.cpuUsage}%</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span>Uptime:</span>
                    <span className="font-medium">{metrics.uptime}</span>
                  </div>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* System Logs */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <FileText className="h-5 w-5" />
            System Logs
          </CardTitle>
          <div className="flex items-center gap-4">
            <Select value={selectedAccount} onValueChange={setSelectedAccount}>
              <SelectTrigger className="w-48">
                <SelectValue placeholder="Filter by account" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Accounts</SelectItem>
                {emailAccounts.map((account) => (
                  <SelectItem key={account.id} value={account.id}>
                    {account.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={logLevel} onValueChange={setLogLevel}>
              <SelectTrigger className="w-32">
                <SelectValue placeholder="Log level" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Levels</SelectItem>
                <SelectItem value="info">Info</SelectItem>
                <SelectItem value="warning">Warning</SelectItem>
                <SelectItem value="error">Error</SelectItem>
                <SelectItem value="success">Success</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardHeader>
        <CardContent>
          <div className="space-y-2 max-h-96 overflow-y-auto">
            {filteredLogs.map((log, index) => (
              <div key={index} className="flex items-start gap-3 p-3 border rounded-lg">
                <Badge className={getLogLevelColor(log.level)}>
                  {log.level}
                </Badge>
                <div className="flex-1">
                  <div className="flex items-center gap-2 text-sm">
                    <span className="text-gray-500">
                      {new Date(log.timestamp).toLocaleString()}
                    </span>
                    <span className="text-gray-400">•</span>
                    <span className="text-gray-500">{log.accountName}</span>
                  </div>
                  <p className="text-sm text-gray-900 dark:text-gray-100 mt-1">
                    {log.message}
                  </p>
                  {log.details && (
                    <details className="mt-2">
                      <summary className="text-xs text-gray-500 cursor-pointer">
                        Show details
                      </summary>
                      <pre className="text-xs text-gray-600 dark:text-gray-400 mt-1 bg-gray-50 dark:bg-gray-800 p-2 rounded">
                        {JSON.stringify(log.details, null, 2)}
                      </pre>
                    </details>
                  )}
                </div>
              </div>
            ))}
          </div>
          {filteredLogs.length === 0 && (
            <div className="text-center py-8">
              <FileText className="h-8 w-8 mx-auto text-gray-400 mb-2" />
              <p className="text-gray-500">No logs found for the selected criteria</p>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default EmailDiagnostics;
